<script lang="ts">
import { LEGAL_LINKS } from "@doc-cheap/legal";
import { page } from "$app/state";
import { COOKIE_SETTINGS_HREF } from "$lib/consent/categories";
import { chromeOf } from "$lib/i18n/copy/chrome";

// The legal bar under every page of the app.
//
// It is in the root layout rather than on the pages that happen to have a
// footer, because what makes a published text part of the agreement is that a
// reader can reach it from wherever they are — the cabinet and the sign-in form
// included, and those have no footer of their own. The documentation renders
// the same bar from the same three addresses (@doc-cheap/site-chrome).
//
// A <nav> and not a <footer>: several pages already end in one, and a second
// would announce a second end of the page to a screen reader. The label is what
// tells this navigation region apart from the top bar's.
//
// The cookie entry is a REAL LINK to the published text rather than a button,
// and that is the whole design: withdrawing consent has to be possible from
// every page, and a control that only works once a script has run would be a
// dead control on the pages that ship no script and for anybody running
// without one. With the consent layer loaded it is intercepted and opens the
// preferences panel instead ($lib/consent/boot.ts); without it, it still leads
// to the page that lists every cookie. It is in the markup from the start, so
// nothing moves when the script arrives.
//
// On a translated page the labels are that language's, the addresses stay the
// English texts — they are the only version that binds — and one sentence says
// so beside them.
const legal = $derived(chromeOf(page.data).legal);
const label = (link: { id: string; label: string }): string =>
  legal[link.id as "terms" | "privacy" | "data-processing"] ?? link.label;
</script>

<nav
  aria-label={legal.label}
  data-testid="legal-bar"
  class="flex flex-wrap gap-x-5 gap-y-2 border-t border-slate-200 px-6 py-4 text-[13px] leading-5 text-slate-500"
>
  {#each LEGAL_LINKS as link (link.id)}
    <a href={link.href} data-testid="legal-{link.id}" class="tap-target-inline hover:text-slate-900 hover:underline">
      {label(link)}
    </a>
  {/each}
  <a
    href={COOKIE_SETTINGS_HREF}
    data-cc="show-preferencesModal"
    data-testid="legal-cookie-settings"
    class="hover:text-slate-900 hover:underline"
  >
    {legal.cookieSettings}
  </a>
  {#if legal.englishOnly !== ""}
    <p class="w-full" data-testid="legal-english-only">{legal.englishOnly}</p>
  {/if}
</nav>
